import { motion, useInView } from "framer-motion";
import { useRef, useState, useEffect } from "react";
import { Brain, ScanSearch, GitCompareArrows, Sparkles, Building2, ArrowDown, Check } from "lucide-react";

const properties = [
  { id: 1, label: "Casa 3q", city: "Campinas", value: "850k" },
  { id: 2, label: "Apto 2q", city: "São Paulo", value: "720k" },
  { id: 3, label: "Sobrado", city: "Valinhos", value: "910k" },
  { id: 4, label: "Terreno", city: "Jundiaí", value: "400k" },
  { id: 5, label: "Cobertura", city: "Santos", value: "1.2M" },
  { id: 6, label: "Casa 4q", city: "Sorocaba", value: "680k" },
];

const steps = [
  {
    icon: ScanSearch,
    title: "Coleta & Análise",
    desc: "A IA mapeia todos os imóveis e preferências de troca na plataforma",
  },
  {
    icon: Brain,
    title: "Machine Learning",
    desc: "O algoritmo calcula scores de compatibilidade entre cada par de imóveis",
  },
  {
    icon: GitCompareArrows,
    title: "Detecção de Ciclos",
    desc: "Algoritmos de grafos identificam permutas em cadeia entre 3+ imóveis",
  },
  {
    icon: Sparkles,
    title: "Matches Inteligentes",
    desc: "Você recebe apenas as melhores oportunidades, ranqueadas por compatibilidade",
  },
];

// Animated grid of properties being "scanned"
const ScanGrid = () => {
  const ref = useRef<HTMLDivElement>(null);
  const isInView = useInView(ref, { once: true, margin: "-100px" });
  const [scannedIdx, setScannedIdx] = useState(-1);
  const [matchedPairs, setMatchedPairs] = useState<number[]>([]);

  useEffect(() => {
    if (!isInView) return;
    // Scan properties one by one
    const scanTimers = properties.map((_, i) =>
      setTimeout(() => setScannedIdx(i), 600 + i * 400)
    );
    // After scanning, highlight matches
    const matchTimer = setTimeout(() => {
      setMatchedPairs([0, 2, 4]);
    }, 600 + properties.length * 400 + 500);

    return () => {
      scanTimers.forEach(clearTimeout);
      clearTimeout(matchTimer);
    };
  }, [isInView]);

  return (
    <div ref={ref} className="grid grid-cols-2 sm:grid-cols-3 gap-3">
      {properties.map((prop, i) => {
        const isScanned = scannedIdx >= i;
        const isMatched = matchedPairs.includes(i);
        return (
          <motion.div
            key={prop.id}
            initial={{ opacity: 0.3, scale: 0.95 }}
            animate={
              isMatched
                ? { opacity: 1, scale: 1, borderColor: "hsl(25 65% 48%)" }
                : isScanned
                ? { opacity: 1, scale: 1 }
                : { opacity: 0.3, scale: 0.95 }
            }
            transition={{ duration: 0.3 }}
            className={`relative p-3 rounded-xl border-2 transition-colors ${
              isMatched
                ? "bg-accent/10 border-accent/40"
                : isScanned
                ? "bg-card border-border"
                : "bg-muted/50 border-transparent"
            }`}
          >
            {isMatched && (
              <motion.div
                initial={{ scale: 0 }}
                animate={{ scale: 1 }}
                className="absolute -top-2 -right-2 w-5 h-5 rounded-full bg-accent flex items-center justify-center"
              >
                <Check size={12} className="text-accent-foreground" />
              </motion.div>
            )}
            <div className="flex items-center gap-2 mb-1">
              <Building2 size={14} className={isMatched ? "text-accent" : "text-muted-foreground"} />
              <span className="text-xs font-display font-semibold text-foreground truncate">{prop.label}</span>
            </div>
            <p className="text-[10px] text-muted-foreground">{prop.city} · R$ {prop.value}</p>

            {/* Scan line effect */}
            {isScanned && !isMatched && scannedIdx === i && (
              <motion.div
                initial={{ top: 0 }}
                animate={{ top: "100%" }}
                transition={{ duration: 0.3 }}
                className="absolute left-0 right-0 h-0.5 bg-accent/60"
              />
            )}
          </motion.div>
        );
      })}
    </div>
  );
};

// Animated connection lines between steps
const StepConnector = ({ delay }: { delay: number }) => (
  <motion.div
    initial={{ scaleY: 0, opacity: 0 }}
    whileInView={{ scaleY: 1, opacity: 1 }}
    viewport={{ once: true }}
    transition={{ delay, duration: 0.4 }}
    className="flex justify-center"
  >
    <div className="flex flex-col items-center gap-0.5">
      <div className="w-px h-6 bg-accent/30" />
      <ArrowDown size={14} className="text-accent/50" />
    </div>
  </motion.div>
);

const AIAlgorithmAnimation = () => {
  return (
    <section className="py-24 bg-background relative overflow-hidden">
      {/* Background decoration */}
      <div className="absolute inset-0 pointer-events-none">
        <div className="absolute top-1/4 left-0 w-72 h-72 rounded-full bg-accent/3 blur-[100px]" />
        <div className="absolute bottom-1/4 right-0 w-96 h-96 rounded-full bg-primary/3 blur-[120px]" />
      </div>

      <div className="container mx-auto px-4 md:px-6 relative z-10">
        {/* Header */}
        <motion.div
          initial={{ opacity: 0, y: 20 }}
          whileInView={{ opacity: 1, y: 0 }}
          viewport={{ once: true }}
          className="text-center mb-16"
        >
          <motion.div
            initial={{ opacity: 0, scale: 0.9 }}
            whileInView={{ opacity: 1, scale: 1 }}
            viewport={{ once: true }}
            className="inline-flex items-center gap-2 px-4 py-1.5 rounded-full bg-primary/10 text-primary text-sm font-display font-medium mb-6 border border-primary/20"
          >
            <Brain size={14} />
            Como funciona por dentro
          </motion.div>

          <h2 className="text-3xl md:text-5xl font-display font-bold text-foreground mb-4 leading-tight">
            O algoritmo em{" "}
            <span className="text-accent">ação</span>
          </h2>
          <p className="text-muted-foreground font-body text-lg max-w-2xl mx-auto leading-relaxed">
            Veja como a Inteligência Artificial da Permuta Digital analisa, compara e encontra
            as melhores oportunidades de permuta em segundos.
          </p>
        </motion.div>

        <div className="grid grid-cols-1 lg:grid-cols-2 gap-16 items-start">
          {/* Left: Step-by-step pipeline */}
          <div className="space-y-0">
            {steps.map((step, i) => (
              <div key={step.title}>
                <motion.div
                  initial={{ opacity: 0, x: -20 }}
                  whileInView={{ opacity: 1, x: 0 }}
                  viewport={{ once: true }}
                  transition={{ delay: i * 0.15 }}
                  className="flex items-start gap-4 group"
                >
                  <motion.div
                    initial={{ scale: 0.8 }}
                    whileInView={{ scale: 1 }}
                    viewport={{ once: true }}
                    transition={{ delay: i * 0.15 + 0.1, type: "spring" }}
                    className="w-12 h-12 rounded-2xl bg-accent/10 border border-accent/20 flex items-center justify-center shrink-0 group-hover:bg-accent/20 transition-colors"
                  >
                    <step.icon size={22} className="text-accent" />
                  </motion.div>
                  <div className="pt-0.5">
                    <div className="flex items-center gap-2 mb-1">
                      <span className="text-[10px] font-display font-bold text-accent/60 uppercase tracking-widest">
                        Etapa {i + 1}
                      </span>
                    </div>
                    <h3 className="text-lg font-display font-bold text-foreground mb-1">
                      {step.title}
                    </h3>
                    <p className="text-sm text-muted-foreground font-body leading-relaxed">
                      {step.desc}
                    </p>
                  </div>
                </motion.div>
                {i < steps.length - 1 && <StepConnector delay={i * 0.15 + 0.2} />}
              </div>
            ))}

            {/* Result highlight */}
            <motion.div
              initial={{ opacity: 0, y: 10 }}
              whileInView={{ opacity: 1, y: 0 }}
              viewport={{ once: true }}
              transition={{ delay: 0.8 }}
              className="mt-6 p-4 rounded-2xl gradient-hero text-primary-foreground"
            >
              <div className="flex items-center gap-3">
                <Sparkles size={20} />
                <div>
                  <p className="font-display font-bold text-sm">Resultado em menos de 3 segundos</p>
                  <p className="text-xs opacity-80 font-body">
                    O algoritmo processa milhares de combinações instantaneamente
                  </p>
                </div>
              </div>
            </motion.div>
          </div>

          {/* Right: Live animation of scanning */}
          <div>
            <motion.div
              initial={{ opacity: 0, y: 20 }}
              whileInView={{ opacity: 1, y: 0 }}
              viewport={{ once: true }}
              transition={{ delay: 0.3 }}
              className="p-6 rounded-2xl bg-card border border-border shadow-card"
            >
              <div className="flex items-center justify-between mb-5">
                <div className="flex items-center gap-2">
                  <div className="w-2 h-2 rounded-full bg-accent animate-pulse" />
                  <span className="text-xs font-display font-medium text-muted-foreground uppercase tracking-wider">
                    Simulação em tempo real
                  </span>
                </div>
                <span className="text-[10px] font-display text-accent font-semibold px-2 py-1 rounded-full bg-accent/10 border border-accent/20">
                  IA ATIVA
                </span>
              </div>

              <ScanGrid />

              {/* Animated stats below */}
              <motion.div
                initial={{ opacity: 0 }}
                whileInView={{ opacity: 1 }}
                viewport={{ once: true }}
                transition={{ delay: 1.5 }}
                className="mt-5 pt-4 border-t border-border"
              >
                <div className="grid grid-cols-3 gap-3 text-center">
                  {[
                    { value: "6", label: "Imóveis analisados" },
                    { value: "15", label: "Pares comparados" },
                    { value: "3", label: "Matches encontrados" },
                  ].map((stat, i) => (
                    <motion.div
                      key={stat.label}
                      initial={{ opacity: 0, y: 5 }}
                      whileInView={{ opacity: 1, y: 0 }}
                      viewport={{ once: true }}
                      transition={{ delay: 1.6 + i * 0.1 }}
                    >
                      <span className="block font-display font-bold text-lg text-accent">{stat.value}</span>
                      <span className="text-[10px] text-muted-foreground font-body">{stat.label}</span>
                    </motion.div>
                  ))}
                </div>
              </motion.div>
            </motion.div>

            {/* Tech note */}
            <motion.p
              initial={{ opacity: 0 }}
              whileInView={{ opacity: 1 }}
              viewport={{ once: true }}
              transition={{ delay: 1.8 }}
              className="text-center text-xs text-muted-foreground font-body mt-4 italic"
            >
              Mesma tecnologia usada em plataformas internacionais de troca de ativos
            </motion.p>
          </div>
        </div>
      </div>
    </section>
  );
};

export default AIAlgorithmAnimation;
